import axios from 'axios';
import { API_BASE_URI } from 'library/constants/api';

function getAuthHeaders() {
  return {
    Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
    'Content-Type': 'application/json',
    'Access-Control-Allow-Origin': '*',
  };
}

export interface HostProfileIdentity {
  bio?: string;
  coverImageUrl?: string;
  avatarImageUrl?: string;
  introVideoUrl?: string;
  introVideoPosterUrl?: string;
  hostingSinceYear?: number;
  instagramHandle?: string;
  instagramUrl?: string;
  facebookHandle?: string;
  facebookUrl?: string;
  linkedinHandle?: string;
  linkedinUrl?: string;
}

export interface HostFeaturedMoment {
  id: number;
  type: 'PHOTO' | 'VIDEO';
  mediaUrl: string;
  propertyId?: number;
  propertyNameSnapshot?: string;
  sortOrder: number;
}

export interface HostAncillaryServiceItem {
  id: number;
  iconKey: string;
  name: string;
  description?: string;
  sortOrder: number;
}

export interface HostLocalPickItem {
  id: number;
  category: 'EAT' | 'EXPLORE' | 'EXPERIENCE' | 'STAY_NEARBY';
  name: string;
  note?: string;
  imageUrl?: string;
  sortOrder: number;
}

export interface HostProfileData {
  hostId: number;
  firstName?: string;
  lastName?: string;
  bio?: string;
  coverImageUrl?: string;
  avatarImageUrl?: string;
  verified: boolean;
  hostingSinceYear?: number;
  introVideoUrl?: string;
  introVideoPosterUrl?: string;
  instagramHandle?: string;
  instagramUrl?: string;
  facebookHandle?: string;
  facebookUrl?: string;
  linkedinHandle?: string;
  linkedinUrl?: string;
  badges: { key: string; label: string; caption?: string }[];
  reviews: { averageRating?: number; totalReviews: number; reviews: any[] };
  properties: any[];
  featuredMoments: HostFeaturedMoment[];
  ancillaryServices: HostAncillaryServiceItem[];
  localPicks: HostLocalPickItem[];
  similarHosts: any[];
  videoReviews: HostVideoReviewItem[];
  testimonials: HostTestimonialItem[];
}

export interface HostVideoReviewItem {
  id: number;
  videoUrl: string;
  posterUrl?: string;
  guestName?: string;
  propertyName?: string;
  quote?: string;
  occasion?: 'SOLO' | 'FAMILY' | 'COUPLE' | 'FRIENDS' | string;
  durationSeconds?: number;
  sortOrder: number;
}

export interface HostTestimonialItem {
  id: number;
  guestName?: string;
  propertyName?: string;
  occasion?: 'SOLO' | 'FAMILY' | 'COUPLE' | 'FRIENDS' | string;
  rating: number;
  snippet: string;
  contextTag?: string;
  sortOrder: number;
  // 'BYS' | 'AIRBNB' — host-only visibility, see TestimonialsTab. Never shown to guests/customers.
  source?: string;
}

export type MediaUploadType =
  | 'avatar'
  | 'cover'
  | 'intro-video'
  | 'intro-video-poster'
  | 'featured'
  | 'local-pick'
  | 'video-review';

class HostProfileService {
  static async saveHostDetails(payload: {
    displayName: string;
    contactNumber: string;
    introduction: string;
  }): Promise<any> {
    const res = await axios.post(
      `${API_BASE_URI}/host/saveHostDetails`,
      payload,
      { headers: getAuthHeaders() },
    );
    return res.data;
  }

  static async saveGstAndBankDetails(formData: FormData): Promise<any> {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
      'Access-Control-Allow-Origin': '*',
    };
    const res = await axios.post(
      `${API_BASE_URI}/host/saveGstAndBankDetails`,
      formData,
      { headers },
    );
    return res.data;
  }

  static async getGstAndBankDetails(): Promise<any> {
    const res = await axios.get(`${API_BASE_URI}/host/getGstAndBankDetails`, {
      headers: getAuthHeaders(),
    });
    return res.data;
  }

  // ─── Host profile page (Premium hosts) ────────────────────────────────────

  static async getMyProfile(): Promise<HostProfileData> {
    const res = await axios.get(`${API_BASE_URI}/host-profile/me`, {
      headers: getAuthHeaders(),
    });
    return res.data;
  }

  static async uploadProfileMedia(type: MediaUploadType, file: File): Promise<string> {
    const formData = new FormData();
    formData.append('type', type);
    formData.append('file', file);
    const res = await axios.post(
      `${API_BASE_URI}/host-profile/media/upload`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
          'Access-Control-Allow-Origin': '*',
        },
      },
    );
    return res.data?.result?.url;
  }

  static async saveIdentity(payload: HostProfileIdentity): Promise<void> {
    await axios.put(`${API_BASE_URI}/host-profile/me/identity`, payload, {
      headers: getAuthHeaders(),
    });
  }

  static async addFeaturedMoment(payload: {
    type: 'PHOTO' | 'VIDEO';
    mediaUrl: string;
    propertyId?: number;
    propertyNameSnapshot?: string;
  }): Promise<HostFeaturedMoment> {
    const res = await axios.post(
      `${API_BASE_URI}/host-profile/me/featured-moments`,
      payload,
      { headers: getAuthHeaders() },
    );
    return res.data;
  }

  static async updateFeaturedMoment(id: number, payload: {
    type: 'PHOTO' | 'VIDEO';
    mediaUrl?: string;
    propertyNameSnapshot?: string;
  }): Promise<void> {
    await axios.put(`${API_BASE_URI}/host-profile/me/featured-moments/${id}`, payload, {
      headers: getAuthHeaders(),
    });
  }

  static async deleteFeaturedMoment(id: number): Promise<void> {
    await axios.delete(`${API_BASE_URI}/host-profile/me/featured-moments/${id}`, {
      headers: getAuthHeaders(),
    });
  }

  static async addAncillaryService(payload: {
    iconKey: string;
    name: string;
    description?: string;
  }): Promise<HostAncillaryServiceItem> {
    const res = await axios.post(`${API_BASE_URI}/host-profile/me/services`, payload, {
      headers: getAuthHeaders(),
    });
    return res.data;
  }

  static async updateAncillaryService(id: number, payload: {
    iconKey: string;
    name: string;
    description?: string;
  }): Promise<void> {
    await axios.put(`${API_BASE_URI}/host-profile/me/services/${id}`, payload, {
      headers: getAuthHeaders(),
    });
  }

  static async deleteAncillaryService(id: number): Promise<void> {
    await axios.delete(`${API_BASE_URI}/host-profile/me/services/${id}`, {
      headers: getAuthHeaders(),
    });
  }

  static async addLocalPick(payload: {
    category: string;
    name: string;
    note?: string;
    imageUrl?: string;
  }): Promise<HostLocalPickItem> {
    const res = await axios.post(`${API_BASE_URI}/host-profile/me/local-picks`, payload, {
      headers: getAuthHeaders(),
    });
    return res.data;
  }

  static async updateLocalPick(id: number, payload: {
    category: string;
    name: string;
    note?: string;
    imageUrl?: string;
  }): Promise<void> {
    await axios.put(`${API_BASE_URI}/host-profile/me/local-picks/${id}`, payload, {
      headers: getAuthHeaders(),
    });
  }

  static async deleteLocalPick(id: number): Promise<void> {
    await axios.delete(`${API_BASE_URI}/host-profile/me/local-picks/${id}`, {
      headers: getAuthHeaders(),
    });
  }

  static async previewPlacePhoto(placeName: string): Promise<string | null> {
    try {
      const res = await axios.get(`${API_BASE_URI}/host-profile/me/local-picks/places/photo`, {
        params: { name: placeName },
        headers: getAuthHeaders(),
      });
      return res.data?.photoUrl ?? null;
    } catch {
      return null;
    }
  }

  // "Live Walkthroughs" (guest-requested call sessions) has been replaced by a per-property
  // walkthrough video gallery — see HostProfileData.properties[].walkthroughVideoUrl, uploaded
  // during property onboarding/edit. There's no session-details save call any more.

  // Video reviews and testimonials are read-only on the host side — they're derived
  // automatically from real, approved guest checkout reviews (see HostProfileService on the
  // backend), so there's no add/update/delete call for them any more. GET /me already returns
  // the current organic set via HostProfileData.videoReviews/.testimonials below.

  static async getMyChatAccessTier(): Promise<'free' | 'premium'> {
    const res = await axios.get(`${API_BASE_URI}/auth/me/chatAccessTier`, {
      headers: getAuthHeaders(),
    });
    return res.data?.chatAccessTier ?? 'free';
  }
}

export default HostProfileService;
